import React from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import Label from "./Label";
import ErrorsForField from "../form/ErrorsForField";
import { useField } from "../form/Form";

import "./BinaryToggle.scss";
import { createDataAttributes } from "../../utilities";

const BinaryToggle = ({
  id,
  name,
  label,
  labelElement,
  value,
  className,
  disabled,
  onChange,
  onBlur,
  onValidate,
  required,
  leftChild,
  rightChild,
  onToggleClick,
  hideLabel,
  noLabel,
  fieldErrors,
  hideErrors,
  requiredError,
  dataAttrib,
  ...rest
}) => {
  useField({
    name,
    validation: { required, requiredError },
    onValidate,
    label
  });

  const toggle = event => {
    const newValue = event.target.checked;
    onChange({
      event,
      field: name,
      value: newValue
    });
    if (onToggleClick) {
      onToggleClick({
        event,
        field: name,
        value: newValue
      });
    }
  };

  const handleBlur = event => {
    onBlur({ event, field: name, value });
  };

  return (
    <div
      {...createDataAttributes(dataAttrib)}
      className={classnames(
        "uikit__binary-toggle__wrapper",
        className,
        id + "-wrapper",
        {
          "uikit__hide-label": hideLabel,
          "uikit__form__hide-errors": hideErrors
        }
      )}
    >
      <Label
        htmlFor={id}
        label={!noLabel && label}
        labelElement={!noLabel && labelElement}
        required={required}
        className="uikit__binary-toggle"
      >
        <div
          className={classnames("uikit__binary-toggle", {
            "uikit__binary-toggle__disabled": disabled,
            "has-error": !!fieldErrors?.length,
            "is-required": required
          })}
        >
          <div
            className={classnames(
              "uikit__binary-toggle__toggle",
              "uikit__binary-toggle__toggle-on",
              {
                "uikit__binary-toggle__selected": value
              }
            )}
          >
            {leftChild}
          </div>
          <div
            className={classnames(
              "uikit__binary-toggle__toggle",
              "uikit__binary-toggle__toggle-off",
              {
                "uikit__binary-toggle__selected": !value
              }
            )}
          >
            {rightChild}
          </div>
          <span className="uikit__binary-toggle__focus-bg"></span>
          <input
            {...rest}
            type="checkbox"
            id={id}
            checked={value}
            value={value}
            onChange={toggle}
            onBlur={handleBlur}
            disabled={disabled}
            aria-describedby={`errors_for_${id}`}
          />
        </div>
      </Label>
      {!hideErrors && !!fieldErrors?.length && (
        <ErrorsForField id={`errors_for_${id}`} errors={fieldErrors} />
      )}
    </div>
  );
};

BinaryToggle.propTypes = {
  //global form props
  className: propTypes.string,
  disabled: propTypes.bool,
  hasError: propTypes.bool,
  id: propTypes.string.isRequired,
  label: propTypes.oneOfType([
    propTypes.string,
    propTypes.object,
    propTypes.element,
    propTypes.func
  ]),
  labelElement: propTypes.element,
  hideLabel: propTypes.bool,
  noLabel: propTypes.bool,
  requiredError: propTypes.string,

  fieldErrors: propTypes.array,
  hideErrors: propTypes.bool,

  name: propTypes.string.isRequired,
  onBlur: propTypes.func.isRequired,
  onChange: propTypes.func.isRequired,
  onValidate: propTypes.func.isRequired,
  required: propTypes.bool,
  value: propTypes.bool.isRequired,

  leftChild: propTypes.oneOfType([propTypes.element, propTypes.string]),
  rightChild: propTypes.oneOfType([propTypes.element, propTypes.string]),
  onToggleClick: propTypes.func,
  componentname: propTypes.string,
  dataAttrib: propTypes.arrayOf(Object)
};

BinaryToggle.defaultProps = {
  onToggleClick: () => {},
  leftChild: "On",
  rightChild: "Off",
  hideErrors: false,
  componentname: "BinaryToggle"
};

export default BinaryToggle;
